﻿import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  List,
  ListItem,
  Button,
  Paper,
} from "@mui/material";
import ShepDataGridPro from "@/components/shared/ShepDataGridPro";
import LoadCollectionGrid from "./LoadCollectionGrid";
import {
  selectSelectedConnection,
  selectSchemaCollectionAdded,
  updateSchema,
  selectSelectedDocumentInfo,
  removeSchemaCollection,
  updateSchemaCollection,
  setSchemaDocumentAdded,
  setTriggerRefreshForConnectionSummary,
  setSchemaCollectionAdded,
  setSelectedTemplateInfo,
  selectSelectedTemplateInfo,
  loadSchemaCollection,
} from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import "./LoadCollection.css";
import UpdateSchemaGrid from "./UpdateSchemaGrid";
import { unwrapResult } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepHorizontalMenu from "@/components/GenericComponents/ShepHorizontalMenu";
import ShepGridWarning from "@/components/GenericComponents/ShepGridWarning";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { LoadingAnimation } from "@/components/shared/LoadingBar";
import {
  EndpointType,
  SchemaType,
} from "@/components/Integrations/ManageConnection/ManageConnectionHelper";
import { FooterButton } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";

const ProvisionedSchema = ({ schemaCollectionName, schemaCollectionType }) => {
  return (
    <Card className="load-collection-grid">
      <Typography variant="h3" sx={{ mb: 2 }}>
        Schema collection installed.
      </Typography>
      <Typography variant="body3" className="load-collection-message-header">
        Provisioned Schema
      </Typography>
      <Typography variant="body3" className="load-collection-message-detail">
        Name:
        <Typography sx={{ paddingLeft: "10px" }}>
          {schemaCollectionName}
        </Typography>
      </Typography>
      <Typography variant="body3" className="load-collection-message-detail">
        Type:
        <Typography sx={{ paddingLeft: "10px" }}>
          {schemaCollectionType}
        </Typography>
      </Typography>
    </Card>
  );
};

const AddTransaction = ({ handleSave, handleBack }) => {
  return (
    <>
      <UpdateSchemaGrid onAdd={handleSave} />
      {/* Footer buttons rendered via portal to FooterBarHost */}
      <FooterButton variant="secondary" onClick={handleBack} sx={{ mr: 1 }}>
        Back
      </FooterButton>
      <FooterButton variant="primary" onClick={handleSave}>
        Save
      </FooterButton>
    </>
  );
};

const LoadCollection = () => {
  const dispatch = useDispatch();

  const [isSchemaAllowed, setIsSchemaAllowed] = useState(false);
  const [isUpdatingSchema, setIsUpdatingSchema] = useState(false);

  const selectedConnection = useSelector(selectSelectedConnection);
  const schemaCollectionInfo = useSelector(selectSchemaCollectionAdded);

  useEffect(() => {
    if (schemaCollectionInfo.length !== 0) {
      if (
        selectedConnection?.EndpointType === EndpointType.RetailChannel ||
        selectedConnection?.EndpointType === EndpointType.WMS
      ) {
        setIsSchemaAllowed(true);
      }
    }
  }, [schemaCollectionInfo]);

  const selectedDocumentInfo = useSelector(selectSelectedDocumentInfo);

  const [showLoadSchema, setShowLoadSchema] = useState(false);
  const [showLoadCollection, setShowLoadCollection] = useState(false);
  const [showLoadedSchema, setShowLoadedSchema] = useState(false);
  const [showUpdateSchema, setShowUpdateSchema] = useState(false);

  const templateInfo = useSelector(selectSelectedTemplateInfo);

  useEffect(() => {
    if (isSchemaAllowed && schemaCollectionInfo.SchemaCollectionId === -1) {
      setShowLoadSchema(true);
    } else if (
      isSchemaAllowed &&
      schemaCollectionInfo.SchemaCollectionId !== -1
    ) {
      setShowLoadedSchema(true);
    }
  }, [isSchemaAllowed, schemaCollectionInfo]);

  const handleLoadSchemaBtn = () => {
    setShowLoadSchema(false);
    setShowLoadCollection(true);
  };

  const handleLoadCollectionBtn = async () => {
    if (!templateInfo || templateInfo.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "Please select a schema template to add for the connection before proceeding!",
          buttonLabel: "Close",
        })
      );
      return;
    }

    try {
      const response = await dispatch(
        loadSchemaCollection({
          templateId: templateInfo.id,
          connectionId: selectedConnection.ConnectionId,
        })
      ).then(unwrapResult);

      if (response.data) {
        console.log("Schema Collection Added", response.data);
        var schemaData = response.data;
        const schemaTypeName =
          Object.keys(SchemaType).find(
            (key) => SchemaType[key] === schemaData.SchemaType
          ) || "Unknown";

        const connectionId = selectedConnection.ConnectionId;
        const schemaCollectionInfo = {
          SchemaCollectionId: response.data.SchemaCollectionId,
          connectionId,
          SchemaTypeName: schemaTypeName,
          SchemaCollectionName: response.data.SchemaCollectionName,
          SchemaType: response.data.SchemaType,
        };
        dispatch(
          setSchemaCollectionAdded({
            schemaCollectionAdded: schemaCollectionInfo,
          })
        );
        setShowLoadCollection(false);
        dispatch(setTriggerRefreshForConnectionSummary());

        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            message: "Schema Collection added successfully!",
            buttonLabel: "Close",
          })
        );

        // heap.io
        window.heap.track("Schema Collection - Load");
      } else {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            message: "Error encountered while adding Schema collection",
            buttonLabel: "Close",
          })
        );
      }
    } catch (error) {
      console.error("Error adding schema collection:", error);
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          message: "Error encountered while adding Schema collection",
          buttonLabel: "Close",
        })
      );
    }
  };

  const handleSaveDocumentInfo = async () => {
    if (selectedDocumentInfo.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "No transactions selected!",
          message: "Please select one or more transactions to Add!",
          buttonLabel: "Close",
        })
      );
      return;
    } else {
      const documentList = selectedDocumentInfo.map((item) => {
        if (item.Direction === "Outbound") {
          return { ...item, ArtifactType: 0 };
        } else {
          return { ...item };
        }
      });

      axios
        .put("/template/endpoint-schema", documentList, {
          params: {
            connectionId: selectedConnection.ConnectionId,
          },
        })
        .then((response) => {
          if (response.data.error === false) {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "result",
                message: response.data.message,
                buttonLabel: "Close",
              })
            );
            dispatch(setSchemaDocumentAdded({ schemaDocumentAdded: true }));
            dispatch(
              updateSchema({
                schemaCollectionId: schemaCollectionInfo.SchemaCollectionId,
                connectionId: selectedConnection.ConnectionId,
              })
            );

            // heap.io
            window.heap.track("Schema Collection - Add Document");
          } else if (response.data.error === true) {
            dispatch(
              createAlert({
                status: "Error",
                alertType: "result",
                title: "Update Schema Unsuccessful!",
                message: response.data.message,
                buttonLabel: "Close",
              })
            );
          }
        });
    }
  };

  const handleBackFunction = () => {
    setShowUpdateSchema(false);
    setShowLoadedSchema(true);
  };

  const handleUpdateSchema = () => {
    dispatch(
      updateSchema({
        schemaCollectionId: schemaCollectionInfo.SchemaCollectionId,
        connectionId: selectedConnection.ConnectionId,
      })
    );
    setShowLoadedSchema(false);
    setShowUpdateSchema(true);
  };

  const validateHandleRemoveSchema = (e) => {
    const connectionId = selectedConnection.ConnectionId;

    axios
      .get("/template/validate-remove-schema", {
        params: {
          connectionId: connectionId,
        },
      })
      .then((response) => {
        if (response.data.warning === true) {
          dispatch(
            createAlert({
              status: "Warning",
              alertType: "result",
              message: response.data.message,
              buttonLabel: "Close",
            })
          );
        } else if (response.data.warning === false) {
          dispatch(
            createAlert({
              status: "Warning",
              alertType: "result",
              title: "Remove Schema Collection",
              message: "Are you sure you want to remove the schema collection?",
              buttonLabel: "Remove",
              onButtonClick: () => handleRemoveSchema(),
              buttonLabel2: "Cancel",
            })
          );
        } else if (response.data.error === true) {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: response.data.message,
              buttonLabel: "Close",
            })
          );
        }
      });
  };

  const handleRemoveSchema = async () => {
    const connectionId = selectedConnection.ConnectionId;
    const schemaCollectionId = schemaCollectionInfo.SchemaCollectionId;
    await dispatch(removeSchemaCollection({ connectionId, schemaCollectionId }))
      .then(unwrapResult)
      .then((response) => {
        if (response.data.error === false) {
          dispatch(setTriggerRefreshForConnectionSummary());
          setShowLoadedSchema(false);
          dispatch(setSelectedTemplateInfo({ selectedTemplateInfo: [] }));
          const schemaCollectionInfo = {
            SchemaCollectionId: -1,
            connectionId,
          };
          dispatch(
            setSchemaCollectionAdded({
              schemaCollectionAdded: schemaCollectionInfo,
            })
          );
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              title: "Remove Successful!",
              message: response.data.message,
              buttonLabel: "Close",
            })
          );

          // heap.io
          window.heap.track("Schema Collection - Delete");
        } else if (response.data.error === true) {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Remove Unsuccessful!",
              message: response.data.message,
              buttonLabel: "Close",
            })
          );
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            title: "Remove Unsuccessful!",
            message: "Error while removing Schema collection",
            buttonLabel: "Close",
          })
        );
      });
  };

  const handleUpdateSchemaCollection = async () => {
    const connectionId = selectedConnection.ConnectionId;
    const schemaCollectionId = schemaCollectionInfo.SchemaCollectionId;
    await performUpdateSchemaCollection(connectionId, schemaCollectionId);
  };

  const performUpdateSchemaCollection = async (connectionId, schemaCollectionId) => {
    setIsUpdatingSchema(true);
    try {
      const response = await dispatch(
        updateSchemaCollection({ connectionId, schemaCollectionId })
      ).then(unwrapResult);

      if (response.data.error === false) {
        dispatch(setTriggerRefreshForConnectionSummary());
        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            title: "Update Successful!",
            message: response.data.message,
            buttonLabel: "Close",
          })
        );

      } else if (response.data.error === true) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            title: "Update Unsuccessful!",
            message: response.data.message,
            buttonLabel: "Close",
          })
        );
      }
    } catch (error) {
      console.error("Error updating schema collection:", error);
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Update Unsuccessful!",
          message: "Error while updating Schema collection",
          buttonLabel: "Close",
        })
      );
    } finally {
      setIsUpdatingSchema(false);
    }
  };

  return (
    <Container>
      {/* for non RetailChannel/WMS */}
      {selectedConnection &&
        selectedConnection.EndpointType !== EndpointType.RetailChannel &&
        selectedConnection.EndpointType !== EndpointType.WMS && (
          <ShepGridWarning
            title="ERP Connection."
            message="Schema cannot be added for an ERP connection!"
            sx={{ minHeight: "100px" }}
          />
        )}

      {/* to show the screen that says "No schema Loaded screen" */}
      {showLoadSchema && (
        <>
          <ShepGridWarning
            title="No Schema Provisioned."
            message="It looks like there is no schema provisioned. To continue, you can:"
            options={[
              "Browse schema collections to explore available options.",
              "Load a selected collection to start editing your schema.",
            ]}
            sx={{ minHeight: "100px" }}
          />

          <FooterButton
            variant="primary"
            onClick={handleLoadSchemaBtn}
            sx={{ marginRight: "10px" }}
          >
            Load Schema
          </FooterButton>
        </>
      )}

      {/* to show the screen having the grid to select a schema */}
      {showLoadCollection && (
        <>
          <LoadCollectionGrid />
          <FooterButton
            variant="primary"
            onClick={handleLoadCollectionBtn}
            sx={{ marginRight: "10px" }}
          >
            Load Collection
          </FooterButton>
        </>
      )}

      {/* to show the screen with selected schema information */}
      {showLoadedSchema && (
        <>
          {isUpdatingSchema && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <LoadingAnimation size={32} />
            </Box>
          )}
          {!isUpdatingSchema && (
            <>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <ShepHorizontalMenu
                  actions={[
                    { label: "Edit", onClick: handleUpdateSchema },
                    { label: "Update", onClick: handleUpdateSchemaCollection },
                    {
                      label: "Remove",
                      onClick: validateHandleRemoveSchema,
                      variant: "danger",
                    },
                  ]}
                />
              </div>
              <ProvisionedSchema
                schemaCollectionName={schemaCollectionInfo.SchemaCollectionName}
                schemaCollectionType={schemaCollectionInfo.SchemaTypeName}
              />
            </>
          )}
        </>
      )}

      {/* to show the screen to add transactions */}
      {showUpdateSchema && (
        <>
          <AddTransaction
            handleSave={handleSaveDocumentInfo}
            handleBack={handleBackFunction}
          />
        </>
      )}
    </Container>
  );
};
export default LoadCollection;
